import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createAppColumnHelper, useClientTable } from "../../../components/table/tableFeatures";
import { VStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import type { AdvertiserAttribute } from "../../../zodios/apiDefinitions/api/walmart";
import { Table } from "../../../components/table/Table";
import { SearchInput } from "../../../components/fields/base/TextField";
import { Button } from "../../../components/core/basic/Button";
import { FaCopy } from "react-icons/fa6";

interface WalmartProfilesStaffTableProps {
  profiles: AdvertiserAttribute[];
}

const columnHelper = createAppColumnHelper<AdvertiserAttribute>();

export function WalmartProfilesStaffTable({ profiles }: WalmartProfilesStaffTableProps) {
  const { t } = useTranslation(["onboarding"]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("advertiserName", {
        header: t(($) => $.onboarding["Advertiser Name"]),
        cell: (info) =>
          info.getValue() || <span className={css({ color: "gray.400" })}>{t(($) => $.onboarding["N/A"])}</span>,
        minSize: 300,
      }),
      columnHelper.accessor("advertiserId", {
        header: t(($) => $.onboarding["Advertiser ID"]),
        cell: (info) => {
          const advertiserId = info.getValue();
          const handleCopy = async () => {
            try {
              await navigator.clipboard.writeText(String(advertiserId));
              // Optional: Show toast notification
            } catch {
              // Silently fail - clipboard permissions may be denied
            }
          };

          return (
            <Button
              variant="link"
              onPress={handleCopy}
              aria-label={t(($) => $.onboarding["Copy advertiser ID {advertiserId} to clipboard"], {
                advertiserId: String(advertiserId),
              })}
            >
              {advertiserId}
              <FaCopy />
            </Button>
          );
        },
        minSize: 150,
      }),
    ],
    [t],
  );

  const table = useClientTable({
    data: profiles,
    columns,
    columnResizeMode: "onChange",
  });

  return (
    <VStack gap="medium" alignItems="stretch">
      <SearchInput
        value={table.state.globalFilter ?? ""}
        onChange={table.setGlobalFilter}
        placeholder={t(($) => $.onboarding["Search profiles..."])}
        aria-label={t(($) => $.onboarding["Search profiles"])}
      />

      <Table table={table} />

      <div className={css({ color: "grey", fontSize: "small" })}>
        {t(($) => $.onboarding["Showing {visibleCount} of {totalCount} profiles"], {
          visibleCount: table.getRowModel().rows.length,
          totalCount: profiles.length,
        })}
      </div>
    </VStack>
  );
}
